/**
 * @fileoverview SectionHeading: a section's heading on a card (B158). The words in the reading face, base size, bold; an optional
 * count beside them in the terracotta of the counts (text-accent-ink); an optional element at the right (This week's job
 * count); a hairline above the section; 16px inset. With `fold` it is a 44px button with a chevron that turns and
 * aria-expanded, inside the h3 so the heading stays a landmark; where the card cannot fold (`canFold` false) it is the plain
 * h3, no button and no chevron. It takes the card's own reading face (no font class of its own).
 *
 * It draws what it is given and decides nothing: whether the section is open, and whether it can fold, are the caller's.
 *
 * Where: the bed card (LocationCard): This week, Plants and Lately (B158 job 5).
 * API: none.
 * Words: none; the caller gives them.
 *
 * @module SectionHeading
 */

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/** The fold: whether the section is open, what a tap does, and whether this card can fold at all. */
export interface SectionHeadingFold {
  /** Whether the section's content is showing. */
  open: boolean;
  /** Folds or unfolds the section. */
  onToggle: () => void;
  /** Whether the card can fold (false: always open, so the heading is plain). */
  canFold: boolean;
}

/** What the heading is given. */
export interface SectionHeadingProps {
  /** A count beside the words (not drawn when absent). */
  count?: number;
  /** An element at the right end (not drawn when absent). */
  right?: React.ReactNode;
  /** Makes the heading a fold; see SectionHeadingFold. */
  fold?: SectionHeadingFold;
  /** Classes from the caller, for its place on the card. */
  className?: string;
  /** The heading's words. */
  children: React.ReactNode;
}

/**
 * The section heading.
 *
 * @param props - SectionHeadingProps.
 */
export const SectionHeading: React.FC<SectionHeadingProps> = ({ count, right, fold, className, children }) => {
  /** The words, the count and the right-hand element, the same in the plain and the folding form. */
  const content = (
    <>
      <span className="flex min-w-0 items-baseline gap-2">
        <span>{children}</span>
        {count !== undefined && <span className="font-bold tabular-nums text-accent-ink">{count}</span>}
      </span>
      {right !== undefined && <span className="ml-auto text-sm font-normal text-muted-foreground">{right}</span>}
    </>
  );
  /** The shared look of the heading's line. */
  const line = 'flex min-h-11 w-full items-center justify-between gap-2 px-4 py-2 text-base font-bold';
  /** The hairline above the section. */
  const hairline = "relative before:absolute before:inset-x-4 before:top-0 before:h-px before:bg-border before:content-['']";

  if (fold?.canFold) {
    return (
      <h3 className={cn('text-base font-bold', hairline, className)}>
        <button
          type="button"
          onClick={fold.onToggle}
          aria-expanded={fold.open}
          className={cn(line, 'text-left text-foreground transition-colors hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
        >
          {content}
          <ChevronDown className={cn('h-5 w-5 shrink-0 transition-transform', fold.open && 'rotate-180')} aria-hidden="true" />
        </button>
      </h3>
    );
  }
  return (
    <h3 className={cn(line, 'text-foreground', hairline, className)}>
      {content}
    </h3>
  );
};
